/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { getSecureRelForTarget, useEuiTheme } from '../../../services';
import { EuiToolTip } from '../../tool_tip';
import { EuiIcon } from '../../icon';
import { euiBetaBadgeStyles } from './beta_badge.styles';
export var COLORS = ['accent', 'subdued', 'hollow'];
export var SIZES = ['s', 'm'];
export var ALIGNMENTS = ['baseline', 'middle'];

// `label` prop can be a `ReactNode` only if `title` or `tooltipContent` is provided

// Must be `type` instead of `interface`
// https://github.com/elastic/eui/issues/6085
export var EuiBetaBadge = ({
  className,
  label,
  color = 'hollow',
  tooltipContent,
  tooltipPosition = 'top',
  anchorProps,
  title,
  iconType,
  onClick,
  onClickAriaLabel,
  href,
  rel,
  target,
  size = 'm',
  alignment = 'baseline',
  ...rest
}) => {
  const euiTheme = useEuiTheme();
  const singleLetter = !!(typeof label === 'string' && label.length === 1);
  const isCircular = iconType || singleLetter;
  const classes = classNames('euiBetaBadge', className);
  const styles = euiBetaBadgeStyles(euiTheme);
  const cssStyles = [styles.euiBetaBadge, styles[color], styles[size], styles[alignment], isCircular ? styles.badgeSizes.circle[size] : styles.badgeSizes.default[size]];
  let icon;
  if (iconType) {
    icon = <EuiIcon css={styles.euiBetaBadge__icon} className="euiBetaBadge__icon" type={iconType} size={size === 'm' ? 'm' : 's'} aria-hidden="true" color="inherit" // forces the icon to inherit its parent color
    />;
  }

  const Element = href ? 'a' : 'button';
  const relObj = {};
  if (href) {
    relObj.href = href;
    relObj.target = target;
    relObj.rel = getSecureRelForTarget({
      href,
      target,
      rel
    });
  }
  if (onClick) {
    relObj.onClick = onClick;
  }
  let content;
  if (onClick || href) {
    content = <Element aria-label={onClickAriaLabel} css={cssStyles} className={classes} title={typeof label === 'string' ? label : title} {...relObj} {...rest}>
        {icon || label}
      </Element>;
    if (tooltipContent) {
      return <EuiToolTip position={tooltipPosition} content={tooltipContent} title={title || label} anchorProps={anchorProps}>
          {content}
        </EuiToolTip>;
    } else {
      return <span {...anchorProps}>{content}</span>;
    }
  } else {
    if (tooltipContent) {
      return <EuiToolTip position={tooltipPosition} content={tooltipContent} title={title || label} anchorProps={anchorProps}>
          <span tabIndex={0} css={cssStyles} className={classes} role="button" {...rest}>
            {icon || label}
          </span>
        </EuiToolTip>;
    } else {
      const spanTitle = title || label;
      if (spanTitle && typeof spanTitle !== 'string') {
        console.warn(`Only string titles are permitted on badges that do not use tooltips. Found: ${typeof spanTitle}`);
      }
      return <span {...anchorProps}>
          <span className={classes} title={spanTitle} css={cssStyles} {...rest}>
            {icon || label}
          </span>
        </span>;
    }
  }
};
EuiBetaBadge.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
     * Will apply an onclick to the badge itself
     */
  onClick: PropTypes.func,
  /**
     * Aria label applied to the onClick button
     */
  onClickAriaLabel: PropTypes.any,
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  /**
     * Supply an icon type if the badge should just be an icon
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenDenseVector", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),
  /**
     * One word label like "Beta" or "Lab"
     */
  /**
     * One word label like "Beta" or "Lab"
     */
  label: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.string.isRequired]).isRequired]).isRequired,
  /**
     * Content for the tooltip
     */
  tooltipContent: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOfType([PropTypes.node, PropTypes.node.isRequired])]),
  /**
     * Custom position of the tooltip
     */
  tooltipPosition: PropTypes.oneOf(["top", "right", "bottom", "left"]),
  /**
     * Passes onto the span wrapping the badge
     */
  anchorProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }),
  /**
     * Optional title will be supplied as tooltip title or title attribute
     * otherwise the label will be used
     */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.string])]),
  /**
     * Accepts accent, subdued and hollow.
     */
  color: PropTypes.any,
  size: PropTypes.any,
  /**
     * Sets the `vertical-align` CSS property
     */
  alignment: PropTypes.any
};
try {
  EuiBetaBadge.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/badge/beta_badge/beta_badge.tsx',
    description: '',
    displayName: 'EuiBetaBadge',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      title: {
        defaultValue: null,
        description: 'Optional title will be supplied as tooltip title or title attribute\n' + 'otherwise the label will be used',
        name: 'title',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClick: {
        defaultValue: null,
        description: 'Will apply an onclick to the badge itself',
        name: 'onClick',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      target: {
        defaultValue: null,
        description: '',
        name: 'target',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      rel: {
        defaultValue: null,
        description: '',
        name: 'rel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      href: {
        defaultValue: null,
        description: '',
        name: 'href',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClickAriaLabel: {
        defaultValue: null,
        description: 'Aria label applied to the onClick button',
        name: 'onClickAriaLabel',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Supply an icon type if the badge should just be an icon',
        name: 'iconType',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'IconType'
        }
      },
      label: {
        defaultValue: null,
        description: 'One word label like "Beta" or "Lab"',
        name: 'label',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string | number | boolean | {} | ReactElement | Iterable<ReactNode> | ... 4 more ... | (ReactPortal & string)'
        }
      },
      tooltipContent: {
        defaultValue: null,
        description: 'Content for the tooltip',
        name: 'tooltipContent',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      tooltipPosition: {
        defaultValue: {
          value: 'top'
        },
        description: 'Custom position of the tooltip',
        name: 'tooltipPosition',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'ToolTipPositions',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }]
        }
      },
      anchorProps: {
        defaultValue: null,
        description: 'Passes onto the span wrapping the badge',
        name: 'anchorProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'CommonProps & HTMLAttributes<HTMLSpanElement>'
        }
      },
      color: {
        defaultValue: {
          value: 'hollow'
        },
        description: 'Accepts accent, subdued and hollow.',
        name: 'color',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"accent" | "subdued" | "hollow"',
          value: [{
            value: '"accent"'
          }, {
            value: '"subdued"'
          }, {
            value: '"hollow"'
          }]
        }
      },
      size: {
        defaultValue: {
          value: 'm'
        },
        description: '',
        name: 'size',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      alignment: {
        defaultValue: {
          value: 'baseline'
        },
        description: 'Sets the `vertical-align` CSS property',
        name: 'alignment',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/badge/beta_badge/beta_badge.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"baseline" | "middle"',
          value: [{
            value: '"baseline"'
          }, {
            value: '"middle"'
          }]
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}